import { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { object, string } from '@calendly/form/validation'
import { useSelector } from 'react-redux'
import { IconCalendar } from '@calendly/ui/components/glyphs/IconCalendar'
import { IconCreditCard } from '@calendly/ui/components/glyphs/IconCreditCard'
import { IconTag } from '@calendly/ui/components/glyphs/IconTag'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { Button } from '@calendly/ui/components/button'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { TextField } from '@calendly/form'
import { httpRequest } from '@calendly/http-tools'
import { HtmlParser } from '@calendly/html-parser'
import { Onetrust } from '@calendly/onetrust/redux'

import { useI18n } from 'locales'
import { useTrack } from 'hooks/useTrack'
import { useTrackPackagePageView } from 'hooks/useTrackPackagePageView'
import { BrandLogo } from 'components/layout/brand_logo'

import { getPackagePrice, getTotalSessionCount } from '../../utils'
import { RenderBranding } from '../branding'
import { stripeAccountSelector } from '../../selectors/payment'
import { isPackagePaymentLink } from '../../utils/package-helpers'
import { reloadIfPaymentsDisabled } from '../../utils/payments-disabled'

import { usePackagePageData } from './PackageContext'
import PackageStripeContainer from './payment/stripe_container'
import { PackageStripeForm } from './payment/package_stripe_form'
import {
  PageContainer,
  ContentWrapper,
  LeftColumn,
  HeaderImageContainer,
  HeaderImage,
  ProfileSection,
  Avatar,
  AvatarImage,
  HostName,
  PackageTitle,
  InfoRowContainer,
  InfoRow,
  IconWrapper,
  SessionCount,
  PackagePrice,
  DescriptionBox,
  Footer,
  LogoWrapper,
  FooterLinks,
  FooterLink,
  RightColumn,
  FormWrapper,
  PaymentSection,
  PaymentSectionTitle,
  TotalDueSection,
  TotalDueLabel,
  TotalDueAmount,
  FormFieldsWrapper,
  BottomSection,
  PaymentTermsText,
  ErrorMessage,
  StyledForm,
} from './styles'
import { TermsAndPrivacyNotice } from './terms_and_privacy_notice'

const PackagePage = () => {
  const { profileSlug, packageUuid } = useParams()
  const history = useHistory()
  const { i18n } = useI18n()
  const isTabletDown = useDeviceType('isTabletDown')
  const [paymentApi, setPaymentApi] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const { packageData, profile, features } = usePackagePageData()
  const stripeAccount = useSelector(stripeAccountSelector)
  const track = useTrack()

  useTrackPackagePageView('package_checkout', packageData)

  const sessionCount = getTotalSessionCount(packageData?.variants?.[0]?.items)
  const packagePrice = getPackagePrice(packageData)
  const isPaymentLink = isPackagePaymentLink(packageData)
  const headerImageUrl = packageData?.header_image_url || profile?.logo_url

  const trackPurchaseProperies = isPaymentLink
    ? {
      cta_key: 'payment_link_checkout_primary_button',
      cta_text: 'purchase',
      cta_destination: 'checkout_success_page',
      action: 'submit_payment_link_purchase',
    } : {
      cta_key: 'meeting_package_checkout_primary_button',
      cta_text: 'purchase',
      cta_destination: 'checkout_success_page',
      action: 'submit_meeting_package_purchase',
    }

  const validationSchema = object({
    name: string()
      .trim()
      .required(i18n.t('booking.package.validation.name_required'))
      .max(155, i18n.t('booking.package.validation.name_max_length')),
    email: string()
      .trim()
      .required(i18n.t('booking.package.validation.email_required'))
      .email(i18n.t('booking.package.validation.email_invalid')),
  })

  const renderFooter = () => (
    <Footer>
      <LogoWrapper>
        <BrandLogo size="16px" tintLegacy />
      </LogoWrapper>
      <FooterLinks>
        {features?.onetrust && (
          <FooterLink type="button" onClick={() => window.OneTrust?.ToggleInfoDisplay()}>
            {i18n.t('booking.commerce_footer.manage_cookies')}
          </FooterLink>
        )}
      </FooterLinks>
    </Footer>
  )

  return (
    <PageContainer className={isSubmitting ? 'submitting' : ''}>
      <RenderBranding badge={{ path: '/' }} />
      {features?.onetrust && (
        <Onetrust
          isEnabled
          domainScript={features.onetrust.data_domain_script}
          hide={features.onetrust.hide}
        />
      )}
      <ContentWrapper className={isSubmitting ? 'submitting' : ''}>
        <LeftColumn>
          {headerImageUrl && (
            <HeaderImageContainer>
              <HeaderImage
                src={headerImageUrl}
                alt={`${packageData?.name || 'Package'} header`}
              />
            </HeaderImageContainer>
          )}
          <ProfileSection>
            {profile?.avatar_url && (
              <Avatar hasheaderimage={!!headerImageUrl}>
                <AvatarImage src={profile.avatar_url} alt={profile.name} />
              </Avatar>
            )}
            <HostName>{profile?.name}</HostName>
            <PackageTitle data-testid="package-name">{packageData?.name || 'Package'}</PackageTitle>

            <InfoRowContainer>
              {!isPaymentLink && (
              <InfoRow>
                <IconWrapper>
                  <UiIcon icon={IconCalendar} size="small" />
                </IconWrapper>
                <SessionCount>
                  {sessionCount}
                  -
                  {i18n.t('booking.package.session_package')}
                </SessionCount>
              </InfoRow>
              )}

              {isPaymentLink && (
                <InfoRow>
                  <IconWrapper>
                    <UiIcon icon={IconTag} size="small" />
                  </IconWrapper>
                  <span>{packageData?.name}</span>
                </InfoRow>
              )}

              <InfoRow>
                <IconWrapper>
                  <UiIcon icon={IconCreditCard} size="small" />
                </IconWrapper>
                <PackagePrice>{packagePrice}</PackagePrice>
              </InfoRow>
            </InfoRowContainer>
          </ProfileSection>

          <DescriptionBox>
            {packageData?.description && (
              <div>{HtmlParser(packageData.description)}</div>
            )}
          </DescriptionBox>

          {!isTabletDown && renderFooter()}
        </LeftColumn>

        <RightColumn>
          <FormWrapper>
            <StyledForm
              initialValues={{
                name: '',
                email: '',
              }}
              validationSchema={validationSchema}
              onSubmit={async (values) => {
                track({
                  name: 'CTA Clicked',
                  dependencies: { user_uuid: packageData?.owner_uuid },
                  properties: trackPurchaseProperies,
                })

                if (isSubmitting || !paymentApi?.createPayment) return

                setIsSubmitting(true)
                setSubmitError(null)

                try {
                  // Payments may have been disabled since page load; re-check
                  // before authorizing the card. A failed check fails open.
                  const [, freshRes] = await httpRequest({
                    url: `/api/booking/packages/${packageUuid}`,
                    method: 'GET',
                  })
                  if (reloadIfPaymentsDisabled(freshRes?.resource)) return

                  // Step 1: Authorize the payment (creates intent with status 'requires_capture')
                  const paymentSuccess = await paymentApi.createPayment()
                  if (!paymentSuccess) {
                    setIsSubmitting(false)
                    return
                  }

                  // Step 2: Get payment data for capture
                  const paymentData = paymentApi.getPaymentData()

                  // Step 3: Capture payment AND create package (atomic server-side operation)
                  // This prevents users from bypassing payment by calling create package directly
                  const [ok, res] = await httpRequest({
                    url: `/api/booking/packages/${packageUuid}/stripe_payment_intents/capture`,
                    method: 'POST',
                    data: {
                      payment_intent_client_secret:
                        paymentData.payment_intent_client_secret,
                      receipt_email: values.email,
                      purchaser_name: values.name,
                      profile_slug: profileSlug,
                    },
                  })

                  if (!ok) {
                    setSubmitError(
                      'Payment capture failed. Please try again or contact support.',
                    )
                    console.error('Failed to capture payment:', res)
                    setIsSubmitting(false)
                    return
                  }

                  // Extract purchased package from response (capture receipt logged server-side)
                  const {
                    purchased_package_uuid: purchasedPackageUuid,
                    purchased_package: purchasedPackage,
                  } = res.resource

                  if (purchasedPackageUuid) {
                    const packageTypeSlug = purchasedPackage.package_type && purchasedPackage.package_type === 'PAYMENT_LINK'
                      ? 'payments'
                      : 'packages'
                    // Navigate to purchase confirmation page
                    history.push(
                      `/${profileSlug}/${packageTypeSlug}/${packageUuid}/purchase/${purchasedPackageUuid}`,
                      {
                        purchasedPackage,
                        packageData,
                        profile,
                      },
                    )
                  } else {
                    setSubmitError(
                      i18n.t('booking.package.errors.payment_complete_error'),
                    )
                    console.error('Failed to create purchased package:', res)
                  }
                } catch (error) {
                  setSubmitError(
                    i18n.t('booking.package.errors.payment_complete_error'),
                  )
                  console.error('Error creating purchased package:', error)
                } finally {
                  setIsSubmitting(false)
                }
              }}
            >
              <FormFieldsWrapper>
                <TextField
                  id="name"
                  name="name"
                  label={i18n.t('booking.package.name_label')}
                  required
                  maxLength={155}
                  placeholder={i18n.t('booking.package.name_placeholder')}
                  autoComplete="name"
                />

                <TextField
                  id="email"
                  name="email"
                  type="email"
                  label={i18n.t('booking.package.email_label')}
                  placeholder={i18n.t('booking.package.email_placeholder')}
                  required
                  autoComplete="email"
                />

                <TotalDueSection>
                  <TotalDueLabel>
                    {i18n.t('booking.package.total_due')}
                  </TotalDueLabel>
                  <TotalDueAmount>{packagePrice}</TotalDueAmount>
                </TotalDueSection>

                <PaymentSection>
                  <PaymentSectionTitle>
                    {i18n.t('booking.package.payment_title')}
                  </PaymentSectionTitle>
                  <PackageStripeContainer stripeAccount={stripeAccount}>
                    <PackageStripeForm registerApi={setPaymentApi} />
                  </PackageStripeContainer>
                </PaymentSection>
              </FormFieldsWrapper>

              <BottomSection>
                <PaymentTermsText>
                  {packageData?.payment_terms}
                </PaymentTermsText>

                {submitError && <ErrorMessage>{submitError}</ErrorMessage>}

                <Button
                  type="submit"
                  wide
                  disabled={isSubmitting || !paymentApi}
                  loading={isSubmitting}
                >
                  {isSubmitting
                    ? i18n.t('booking.package.processing_button')
                    : i18n.t('booking.package.purchase_button')}
                </Button>
                <TermsAndPrivacyNotice />
              </BottomSection>
            </StyledForm>

            {isTabletDown && renderFooter()}
          </FormWrapper>
        </RightColumn>
      </ContentWrapper>
    </PageContainer>
  )
}

export default PackagePage
